// Date maths for the project schedule (Gantt). Dates are 'YYYY-MM-DD' strings; financial years run April–March.
import dayjs from 'dayjs';

export const DAY_MS = 86400000;
const toMs = (ymd) => Date.parse(`${ymd}T00:00:00Z`);
export const daysBetween = (a, b) => Math.round((toMs(b) - toMs(a)) / DAY_MS);
export const durationDays = (start, end) => (start && end ? daysBetween(start, end) + 1 : null);

const ymd = (d) => d.format('YYYY-MM-DD');
const minOf = (list) => list.filter(Boolean).sort()[0] || null;
const maxOf = (list) => list.filter(Boolean).sort().reverse()[0] || null;

// Financial year that a date falls in, named by its starting year (Apr 2026 – Mar 2027 → 2026)
export const fyOf = (date) => {
    const d = dayjs(date);
    return d.month() >= 3 ? d.year() : d.year() - 1;
};
export const fyRange = (startYear) => ({
    key: `fy${startYear}`,
    startYear,
    start: `${startYear}-04-01`,
    end: `${startYear + 1}-03-31`,
    label: `FY ${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`,
});

// Every date the schedule shows, to size the chart and the year tabs
const planDates = (plan) => {
    const steps = plan.steps || [];
    const subs = steps.flatMap(s => s.sub_tasks || []);
    return [
        plan.project?.start_date, plan.project?.end_date, plan.handover_date, plan.contract?.end,
        ...steps.flatMap(s => [s.start_date, s.end_date]),
        ...subs.flatMap(s => [s.start_date, s.end_date]),
        ...(plan.invoices || []),
    ].filter(Boolean);
};

// "Project" tab range: the project's own work and handover, padded out to whole weeks (Monday start)
export const projectRange = (plan) => {
    const steps = plan.steps || [];
    const subs = steps.flatMap(s => s.sub_tasks || []);
    const dates = [plan.project?.start_date, plan.project?.end_date, plan.handover_date, ...steps.flatMap(s => [s.start_date, s.end_date]), ...subs.flatMap(s => [s.start_date, s.end_date])].filter(Boolean);
    const today = ymd(dayjs());
    let start = minOf(dates) || today;
    let end = maxOf(dates) || ymd(dayjs().add(8, 'week'));
    const s = dayjs(start);
    start = ymd(s.subtract((s.day() + 6) % 7, 'day'));
    const e = dayjs(end);
    end = ymd(e.add(6 - ((e.day() + 6) % 7), 'day'));
    if (daysBetween(start, end) < 27) end = ymd(dayjs(start).add(27, 'day'));
    return { start, end };
};

// One tab per financial year from the project start to the end of the contract (or the last known date)
export const financialYears = (plan) => {
    const dates = planDates(plan);
    const today = ymd(dayjs());
    const first = fyOf(minOf(dates) || today);
    const last = Math.max(first, fyOf(maxOf(dates) || today));
    return Array.from({ length: Math.min(12, last - first + 1) }, (_, i) => fyRange(first + i));
};

// Header columns: weeks for the project tab, months for a financial year
export const columnsFor = (range, unit) => {
    const cols = [];
    let d = dayjs(range.start);
    const end = dayjs(range.end);
    while (!d.isAfter(end)) {
        const next = unit === 'month' ? d.add(1, 'month').startOf('month') : d.add(7, 'day');
        const last = next.subtract(1, 'day');
        cols.push({
            start: ymd(d),
            end: ymd(last.isAfter(end) ? end : last),
            label: unit === 'month' ? d.format('MMM') : d.format('D MMM'),
            sub: unit === 'month' ? d.format('YYYY') : null,
            band: cols.length % 2 === 1,
        });
        d = next;
    }
    return cols;
};

// Position of a date / span as % of the range; null when it falls outside
export const posPct = (date, range) => {
    if (!date || date < range.start || date > range.end) return null;
    return (daysBetween(range.start, date) + 0.5) / (daysBetween(range.start, range.end) + 1) * 100;
};
export const spanPct = (start, end, range) => {
    if (!start) return null;
    const s = start < range.start ? range.start : start;
    const e = (end || start) > range.end ? range.end : (end || start);
    if (s > range.end || e < range.start || e < s) return null;
    const total = daysBetween(range.start, range.end) + 1;
    return {
        left: daysBetween(range.start, s) / total * 100,
        width: (daysBetween(s, e) + 1) / total * 100,
        clipLeft: start < range.start,
        clipRight: (end || start) > range.end,
    };
};

export const stepPercent = (step) => {
    if (step.total_sub_tasks) return Math.round(step.completed_sub_tasks / step.total_sub_tasks * 100);
    return step.status === 'done' ? 100 : 0;
};

export const STEP_STYLE = {
    done: { bar: '#008080', text: '#fff', label: 'Done' },
    now: { bar: '#5eb5ad', text: '#fff', label: 'In progress' },
    on_hold: { bar: '#e0a72e', text: '#fff', label: 'On hold' },
    upcoming: { bar: '#cbd5e1', text: '#334155', label: 'Not started' },
};

export const SUB_STATUS_BAR = {
    Completed: '#008080',
    'In Progress': '#5eb5ad',
    'On Hold': '#e0a72e',
    Pending: '#cbd5e1',
};
